import { useMemo } from "react";
import katex from "katex";

/**
 * Render de KaTeX.
 *
 * `throwOnError: false` es deliberado: una fórmula mal formada por el backend
 * debe verse como texto en rojo, no tumbar el stream entero.
 */

const KATEX_OPTIONS = {
  throwOnError: false,
  strict: false as const,
  trust: false,
  output: "html" as const,
};

/**
 * Siglas del curso: `VAN`, `TIR`, `WACC`, `CTN`…
 *
 * En LaTeX, escribir `WACC` a secas se compone como el producto de cuatro
 * variables —W·A·C·C, en cursiva y con espaciado— y deja de leerse como el
 * nombre de un indicador. Como el agente emite las fórmulas con la notación
 * del curso, aquí se envuelven esas siglas en `\text{}` antes de renderizar:
 * la fórmula se sigue escribiendo igual en el payload y se lee como debe.
 *
 * El orden del patrón importa: primero los `\text{…}` ya escritos y los
 * comandos (`\frac`, `\alpha`), que se devuelven intactos; solo lo que quede
 * suelto y sea una sigla se envuelve.
 */
const COURSE_NOTATION =
  /\\text\{[^{}]*\}|\\[a-zA-Z]+|\b(?:VAN|TIR|WACC|CTN|NOF|FCL|NOPAT|EBITDA|ROE|ROA|PER|CAPEX|EBIT|VPN|VA|VF)\b/g;

export function normalizeCourseNotation(latex: string): string {
  return latex.replace(COURSE_NOTATION, (match) =>
    match.startsWith("\\") ? match : `\\text{${match}}`,
  );
}

export interface TexProps {
  latex: string;
  display?: boolean;
  className?: string;
}

export function Tex({ latex, display = false, className }: TexProps) {
  const html = useMemo(() => {
    try {
      return katex.renderToString(normalizeCourseNotation(latex), {
        ...KATEX_OPTIONS,
        displayMode: display,
      });
    } catch (error) {
      return `<span class="tex--error">${escapeHtml(
        error instanceof Error ? error.message : "Fórmula no válida",
      )}</span>`;
    }
  }, [latex, display]);

  return (
    <span
      className={[display ? "tex tex--display" : "tex", className ?? ""].filter(Boolean).join(" ")}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * Texto con matemática inline escrita entre `$…$`. Es lo que usan los pasos de
 * un ejemplo resuelto y las notas de un simulador, donde el LaTeX convive con
 * prosa y no hay un parser de markdown de por medio.
 */
export function TexText({ text, className }: { text: string; className?: string }) {
  const segments = useMemo(() => splitMath(text), [text]);
  return (
    <span className={className}>
      {segments.map((segment, index) =>
        segment.kind === "math" ? (
          <Tex key={index} latex={segment.value} />
        ) : (
          <span key={index}>{segment.value}</span>
        ),
      )}
    </span>
  );
}

/** Un campo de fórmula admite LaTeX puro, delimitado o texto legible. */
export function TexFormula({ text }: { text: string }) {
  const value = text.trim();
  const wrappers = [
    ["$$", "$$"], ["$", "$"], ["\\[", "\\]"], ["\\(", "\\)"],
  ];
  for (const [start, end] of wrappers) {
    if (value.startsWith(start) && value.endsWith(end)) {
      const expression = value.slice(start.length, -end.length);
      if (!start.includes("$") || !expression.includes("$")) {
        return <Tex latex={expression} />;
      }
    }
  }
  if (/\\(?:[a-zA-Z]+|[%{} ])/.test(value) && !value.includes("$")) {
    return <Tex latex={value} />;
  }
  return <TexText text={text} />;
}

interface Segment {
  kind: "text" | "math";
  value: string;
}

/** Parte el texto en tramos de prosa y tramos `$…$` / `$$…$$`. */
function splitMath(text: string): Segment[] {
  const segments: Segment[] = [];
  const pattern = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) {
      segments.push({ kind: "text", value: text.slice(cursor, match.index) });
    }
    const math = match[1] ?? match[2] ?? "";
    segments.push({ kind: "math", value: math });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) segments.push({ kind: "text", value: text.slice(cursor) });
  return segments.length > 0 ? segments : [{ kind: "text", value: text }];
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
